import React, { useState } from 'react';
import ChevronLeftIcon from 'components/Icons/ChevronLeftIcon';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import type { CardFilterStateApi } from 'components/FilterPanel/cardFilterUrlState';
import MobileFilterSheet from 'screens/Main/Search/Components/MobileFilterSheet';
import type { FinishCounts } from 'utils/finishFilter';
import type { FocusSide } from 'utils/offerFocusState';
import { useOfferBuilder } from '../context/OfferBuilderContext';
import SourcePanel, { SourceMode } from './SourcePanel';
import OfferPhoneCartBar from './OfferPhoneCartBar';
import OfferPhoneCartSheet from './OfferPhoneCartSheet';
import type OfferColumn from './OfferColumn';

interface OfferPhoneFocusViewProps {
  side: FocusSide;
  counterpartyUsername: string;
  filters: CardFilterStateApi;
  finishCounts?: FinishCounts;
  /** The focused list's filtered total, for the filter sheet's apply label. */
  totalCount?: number;
  gridSize: CardSize;
  onGridSizeChange: (size: CardSize) => void;
  cardSize: CardSize;
  emptyGuidance: React.ComponentProps<typeof OfferColumn>['emptyGuidance'];
  nextLabel: string;
  /** The back chevron: Show both sides, returns to the step. */
  onBack: () => void;
  /** The cart sheet's Next: leaves the grid and advances the step. */
  onNext: () => void;
  onModeChange: (mode: SourceMode) => void;
  onResultMeta: (meta: { finishCounts?: FinishCounts; totalCount?: number }) => void;
  onProposerOffersDisabled?: () => void;
  /** The mode the step panel was on when the grid opened (the full list, by construction). */
  initialMode: SourceMode;
}

/**
 * The phone full-screen grid (Asana 1219788320590626 Decision 14): the
 * focused SourcePanel in place inside the app shell (the top bar stays
 * reachable), the cart collapsed into a bottom bar that also carries
 * Filters, a cart sheet, and the shared MobileFilterSheet with the Finish
 * group. No strips, no rail, no nav fold on phones.
 */
export const OfferPhoneFocusView: React.FC<OfferPhoneFocusViewProps> = ({
  side,
  counterpartyUsername,
  filters,
  finishCounts,
  totalCount,
  gridSize,
  onGridSizeChange,
  cardSize,
  emptyGuidance,
  nextLabel,
  onBack,
  onNext,
  onModeChange,
  onResultMeta,
  onProposerOffersDisabled,
  initialMode,
}) => {
  const { youSend, theySend, youSendTotal, theySendTotal } = useOfferBuilder();
  const [cartOpen, setCartOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const cartSide = side === 'send' ? 'youSend' : 'theySend';
  const cartLabel = side === 'send' ? 'You send' : 'They send';
  const listNoun = side === 'send' ? 'want list' : 'have list';
  const count = side === 'send' ? youSend.length : theySend.length;
  const total = side === 'send' ? youSendTotal : theySendTotal;
  // Stored M (from a wide session) clamps to S: phones offer S and L only.
  const phoneSize: CardSize = gridSize === 'M' ? 'S' : gridSize;

  return (
    <div className="offer-phone-focus" data-testid="offer-phone-focus">
      <div className="offer-phone-focus-bar">
        <button
          type="button"
          className="offer-phone-back"
          onClick={onBack}
          aria-label="Show both sides"
          data-testid="offer-phone-back"
        >
          <ChevronLeftIcon size={16} color="#ffffff" />
        </button>
        <div className="offer-phone-focus-title">
          {counterpartyUsername}&apos;s {listNoun}
        </div>
      </div>

      <SourcePanel
        side={side}
        focused
        phone
        filters={filters}
        gridSize={phoneSize}
        onGridSizeChange={onGridSizeChange}
        onShowBoth={onBack}
        onModeChange={onModeChange}
        onResultMeta={onResultMeta}
        onProposerOffersDisabled={onProposerOffersDisabled}
        initialMode={initialMode}
      />

      <OfferPhoneCartBar
        count={count}
        label={cartLabel}
        totalPoints={total}
        onOpen={() => setCartOpen(true)}
        filtersCount={filters.tokens.length}
        onOpenFilters={() => setFiltersOpen(true)}
      />

      {cartOpen && (
        <OfferPhoneCartSheet
          side={cartSide}
          title={cartLabel}
          emptyGuidance={emptyGuidance}
          cardSize={cardSize}
          nextLabel={nextLabel}
          onClose={() => setCartOpen(false)}
          onNext={() => {
            setCartOpen(false);
            onNext();
          }}
        />
      )}

      {filtersOpen && (
        <MobileFilterSheet
          state={filters.state}
          onChange={filters.update}
          onClearAll={filters.clearAll}
          onClose={() => setFiltersOpen(false)}
          totalCount={totalCount}
          hideTradeSection
          showFinish
          finishCounts={finishCounts}
          subtitle={
            <>
              Filtering <strong>{counterpartyUsername}&apos;s {listNoun}</strong>
            </>
          }
        />
      )}
    </div>
  );
};

export default OfferPhoneFocusView;
